import React, { useState } from "react";
import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import { FileCopyOutlined, Check } from "@material-ui/icons";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Text } from "shared";
import { Colors } from "theme";
import { TABLE_COLUMNS } from "../constants";
import { Referral } from "../queries/types";
import EditableCell from "./EditableCell";
import StatusChip from "./StatusChip";

// Sticky column left offsets, derived from cumulative column minWidths:
//   [0] = 0                — checkbox column start
//   [1] = 42               — checkbox width (42px)
//   [2] = 42 + 120 = 162   — checkbox (42px) + Status minWidth (120px)
//   [3] = 162 + 160 = 322  — above + Special Status minWidth (160px)
const STICKY_OFFSETS = [0, 42, 162, 322];
const STICKY_COUNT = 3; // number of data columns to freeze (status, special_status, patient_name)

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxHeight: "calc(100vh - 340px)",
    overflow: "auto",
  },
  table: {
    minWidth: 1800,
  },
  headCell: {
    whiteSpace: "nowrap",
    fontWeight: 600,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.grey[100],
    zIndex: 2,
  },
  stickyHeadCell: {
    position: "sticky",
    top: 0,
    zIndex: 4,
    backgroundColor: theme.palette.grey[100],
  },
  stickyBodyCell: {
    position: "sticky",
    zIndex: 1,
  },
  lastStickyHead: {
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
  },
  lastStickyBody: {
    boxShadow: "2px 0 4px rgba(0,0,0,0.06)",
  },
  bodyCell: {
    whiteSpace: "nowrap",
    fontSize: 14,
  },
  nhsNumber: {
    fontFamily: "monospace",
    fontSize: 14,
  },
  copyWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  copyBtn: {
    padding: theme.spacing(0.25),
    opacity: 0,
    transition: "opacity 0.15s",
    "$copyWrap:hover &": {
      opacity: 0.6,
    },
  },
  copyBtnDone: {
    padding: theme.spacing(0.25),
    color: theme.palette.success.main,
  },
  copyIcon: {
    fontSize: theme.typography.body2.fontSize,
  },
  emptyRow: {
    textAlign: "center",
    padding: theme.spacing(6),
  },
}));

interface ReferralTrackingTableProps {
  referrals: Referral[];
  sortBy: string;
  sortDirection: "asc" | "desc";
  onSortChange: (field: string) => void;
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  onCellSave: (id: number, field: string, value: string | null) => void;
  onRowClick: (referral: Referral) => void;
}

const formatNhsNumber = (nhs: string | null): string => {
  if (!nhs) return "-";
  const digits = String(nhs).replace(/\D/g, "");
  if (digits.length !== 10) return nhs;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const formatBodyParts = (value: string | null): string => {
  if (!value) return "-";
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.join(", ");
  } catch {
    // Value is plain text, not JSON — expected for legacy data
  }
  return value;
};

const getDisplayStatus = (row: Referral): string => {
  if (row.special_status && row.special_status.trim()) {
    return row.special_status;
  }
  return row.status || "";
};

const statusRowColors: Record<string, string> = {
  Chase: "rgba(255, 152, 0, 0.06)",
  "Signed Up": "rgba(33, 150, 243, 0.06)",
  "In Treatment": "rgba(76, 175, 80, 0.06)",
  "Never Signed Up": "rgba(244, 67, 54, 0.04)",
};

const ReferralTrackingTable: React.VFC<ReferralTrackingTableProps> = ({
  referrals,
  sortBy,
  sortDirection,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onCellSave,
  onRowClick,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const allSelected = referrals.length > 0 && referrals.every(r => selectedIds.has(r.id));
  const someSelected = referrals.some(r => selectedIds.has(r.id));

  const handleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      referrals.forEach(r => next.delete(r.id));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedIds);
      referrals.forEach(r => next.add(r.id));
      onSelectionChange(next);
    }
  };

  const handleSelectRow = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  const renderCellValue = (row: Referral, col: (typeof TABLE_COLUMNS)[number]) => {
    const value = row[col.key as keyof Referral] as string | null;

    if (col.editable && col.type) {
      return <EditableCell value={value} field={col.key} rowId={row.id} type={col.type} onSave={onCellSave} />;
    }

    if (col.key === "status") {
      const displayStatus = getDisplayStatus(row);
      return <StatusChip status={displayStatus} />;
    }

    if (col.key === "nhs_number") {
      const formatted = formatNhsNumber(value);
      const raw = value ? String(value).replace(/\D/g, "") : "";
      if (!raw) return <span className={classes.nhsNumber}>-</span>;
      const key = `nhs-${row.id}`;
      const copied = copiedKey === key;
      return (
        <span className={classes.copyWrap}>
          <span className={classes.nhsNumber}>{formatted}</span>
          <IconButton
            className={copied ? classes.copyBtnDone : classes.copyBtn}
            size="small"
            onClick={() => handleCopy(raw, key)}
          >
            {copied ? <Check className={classes.copyIcon} /> : <FileCopyOutlined className={classes.copyIcon} />}
          </IconButton>
        </span>
      );
    }

    if (col.key === "patient_id") {
      if (!value) return <span>-</span>;
      const key = `pid-${row.id}`;
      const copied = copiedKey === key;
      return (
        <span className={classes.copyWrap}>
          <span>{value}</span>
          <IconButton
            className={copied ? classes.copyBtnDone : classes.copyBtn}
            size="small"
            onClick={() => handleCopy(value, key)}
          >
            {copied ? <Check className={classes.copyIcon} /> : <FileCopyOutlined className={classes.copyIcon} />}
          </IconButton>
        </span>
      );
    }

    if (col.key === "body_parts_referred") {
      return <span>{formatBodyParts(value)}</span>;
    }

    if (col.key === "wrong_joint" || col.key === "wrong_site") {
      if (!value) return null;
      return (
        <Text tiny error>
          {value}
        </Text>
      );
    }

    return <span>{value ?? "-"}</span>;
  };

  if (referrals.length === 0) {
    return (
      <TableContainer className={classes.container}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} padding="checkbox" />
              {TABLE_COLUMNS.map(col => (
                <TableCell key={col.key} className={classes.headCell}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={TABLE_COLUMNS.length + 1} className={classes.emptyRow}>
                <Text secondary>{t("referral_tracking.no_referrals")}</Text>
                <br />
                <Text tiny secondary>
                  {t("referral_tracking.no_referrals_hint")}
                </Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={classes.container}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell
              className={`${classes.headCell} ${classes.stickyHeadCell}`}
              padding="checkbox"
              style={{ left: STICKY_OFFSETS[0] }}
            >
              <Checkbox
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                onChange={handleSelectAll}
                size="small"
              />
            </TableCell>
            {TABLE_COLUMNS.map((col, idx) => {
              const isSticky = idx < STICKY_COUNT;
              const isLastSticky = idx === STICKY_COUNT - 1;
              return (
                <TableCell
                  key={col.key}
                  className={`${classes.headCell}${isSticky ? ` ${classes.stickyHeadCell}` : ""}${isLastSticky ? ` ${classes.lastStickyHead}` : ""}`}
                  style={{
                    ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                    ...(isSticky ? { left: STICKY_OFFSETS[idx + 1] } : {}),
                  }}
                  sortDirection={col.sortable && sortBy === col.key ? sortDirection : false}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortBy === col.key}
                      direction={sortBy === col.key ? sortDirection : "asc"}
                      onClick={() => onSortChange(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {referrals.map(row => {
            const displayStatus = getDisplayStatus(row);
            const bgColor = statusRowColors[displayStatus];
            const isSelected = selectedIds.has(row.id);

            return (
              <TableRow
                key={row.rowKey}
                hover
                selected={isSelected}
                style={{ backgroundColor: isSelected ? undefined : bgColor || "transparent", cursor: "pointer" }}
                onClick={e => {
                  // Don't open drawer when clicking checkboxes, editable cells, or buttons
                  const target = e.target as HTMLElement;
                  if (target.closest("input, select, button, a, [role='button']")) return;
                  onRowClick(row);
                }}
              >
                <TableCell
                  padding="checkbox"
                  className={classes.stickyBodyCell}
                  style={{ left: STICKY_OFFSETS[0], backgroundColor: Colors.PAPER }}
                >
                  <Checkbox checked={isSelected} onChange={() => handleSelectRow(row.id)} size="small" />
                </TableCell>
                {TABLE_COLUMNS.map((col, idx) => {
                  const isSticky = idx < STICKY_COUNT;
                  const isLastSticky = idx === STICKY_COUNT - 1;
                  return (
                    <TableCell
                      key={col.key}
                      className={`${classes.bodyCell}${isSticky ? ` ${classes.stickyBodyCell}` : ""}${isLastSticky ? ` ${classes.lastStickyBody}` : ""}`}
                      style={{
                        ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                        ...(isSticky ? { left: STICKY_OFFSETS[idx + 1], backgroundColor: Colors.PAPER } : {}),
                      }}
                    >
                      {renderCellValue(row, col)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReferralTrackingTable;
